import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRefresh } from '../../../context/RefreshContext';
import { useTranslation } from 'react-i18next';
import { LoadingProvider, useLoading } from '../../../context/LoadingContext';
import AddIcon from '@mui/icons-material/Add';
import CustomSearch from '../../../components/CustomSearch';
import { exportEventsCSV, getEventsById } from '../../../axios/Visitor/VisitorApi';
import VisitorRoster from './VisitorRoster';
import { CustomAdvancedMenuList, CustomTabPanel, CustomTabsV2 } from '../../../components/StyledComponents';
import {
  ClickAwayListener,
  Grow,
  Link,
  MenuItem,
  Paper,
  Popper,
  Tab,
} from "@mui/material";
import ExportIcon16pxSvg from '../../../assets/images/icons/ExportIcon16pxSvg';
import ImportIcon20pxSvg from '../../../assets/images/icons/ImportIcon20pxSvg';
import AddVisitorEventDialog from './AddVisitorEventDialog';
import { getUserList } from '../../../axios/User/UserApi';
import { toast } from 'react-toastify';
import VisitorRosterDetail from './VisitorRosterDetail';
import VisitorUserGridInfoTable from './VisitorUserGridInfoTable';
import VisitorUserInfo from './VisitorUserInfo';
import { useAuth } from '../../../context/AuthContext';
import CustomSplitButton from '../../../components/CustomSplitButton';
import { useSearchParams } from "react-router-dom";
import ImportIcon16pxSvg from '../../../assets/images/icons/ImportIcon16pxSvg';

const VisitorManagement = () => {
  const { refreshData, handleRefreshData } = useRefresh();
  const { t } = useTranslation();
  const [show, hide] = useLoading();
  const [viewMode, setViewMode] = useState('list');
  const anchorRefAdvanced = useRef(null);
  const [openAdvanced, setOpenAdvanced] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRosterDetail, setSelectedRosterDetail] = useState(null);
  const [openAddRoster, setOpenAddRoster] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [visitorUserTotal, setVisitorUserTotal] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const { profile, dateTimeFormat, genaralSettingDateFormat, genaralSettingTimeFormat } = useAuth();
  const [searchParams] = useSearchParams();

  const [eventTotalPages, setEventTotalPages] = useState(1);
  const [eventPageSize, setEventPageSize] = useState(20);

  const eventId = searchParams.get("eventId");
  const eventVisitorId = searchParams.get("eventVisitorId");

  const handleRefresh = () => {
    setRefresh((prev) => !prev);
  };

  function getCurrentTimestamp() {
    const now = new Date();
    const yyyy = now.getFullYear();
    const MM = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const HH = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
  }

  const exportEvents = async () => {
    setOpenAdvanced(false);

    const payload = {
      pageNo: eventTotalPages,
      recordPerPage: eventPageSize,
      searchText: debouncedSearchText || "",
      langId: localStorage.getItem("i18nextLngId"),
      formatDate: dateTimeFormat,
      dateFormat: genaralSettingDateFormat,
      timeFormat: genaralSettingTimeFormat,
    };

    try {
      show();
      const response = await exportEventsCSV(payload);

      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('export.visitor-events')}_${getCurrentTimestamp()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      toast.error(error?.response?.data?.message || t('common.something-went-wrong'));
    } finally {
      hide();
    }
  };

  const fetchUserData = async () => {
    try {
      show();
      const payload = {
        statusId: 1,
        page: 1,
        pageSize: 10000,
        status: null,
        langId: localStorage.getItem("i18nextLngId"),
      };
      const response = await getUserList(payload);
      if (
        response.data.message === "Successful" ||
        response.data.message === "Thành công"
      ) {
        setUsersData(
          response.data.data.filter(
            (user) =>
              user.administratorRights === "ADMIN"
          )
        );
        hide();
      }
    } catch (error) {
      hide();
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
      console.error("Error fetching data:", error);
    } finally {
      hide();
    }
  };

  const handleCloseAdvanced = (event) => {
    if (
      anchorRefAdvanced.current &&
      anchorRefAdvanced.current.contains(event.target)
    ) {
      return;
    }
    setOpenAdvanced(false);
  };

  useEffect(() => {
    if (eventId) {
      loadEventDetail(eventId);
    }
  }, [eventId]);

  const loadEventDetail = async (eventId) => {
    try {
      show();

      const payload = {
        id: Number(eventId),
        companyId: profile?.companyId,
        langId: localStorage.getItem("i18nextLngId"),
      };

      const res = await getEventsById(payload);

      if (res?.data?.responseCode === 0) {
        setSelectedRosterDetail(res.data?.data?.[0]);
        setViewMode("detail");
      }
    } finally {
      hide();
    }
  };

  const handleRosterRowSelection = useCallback(async (newSelection) => {
    if (!newSelection?.id) return;

    try {
      show();

      const payload = {
        id: newSelection.id,
        companyId: profile?.companyId,
        langId: localStorage.getItem("i18nextLngId"),
      };

      const res = await getEventsById(payload);

      if (res?.data?.responseCode === 0) {
        setSelectedRosterDetail(res.data?.data?.[0]);
        setViewMode('detail');
      } else {
        toast.error(
          res?.data?.message ||
          t("common.something-went-wrong")
        );
      }

    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
        t("common.something-went-wrong")
      );
    } finally {
      hide();
    }
  }, [show, hide, t, profile?.companyId]);

  const handleToggleAdvanced = () => {
    setOpenAdvanced((prev) => !prev);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchText]);

  const handleQuickFilterChange = (value) => {
    setSearchText(value);
  };

  useEffect(() => {
    fetchUserData();
  }, [refreshData]);

  const handleOpenAddRoster = () => {
    setOpenAddRoster(true);
  };

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const a11yProps = (index) => {
    return {
      id: `user-tab-${index}`,
      'aria-controls': `user-tabpanel-${index}`,
    };
  };

  const handlePaginationChange = ({ totalPages, pageSize }) => {
    setEventTotalPages(totalPages);
    setEventPageSize(pageSize);
  };

  return (
    <>
      <div className="visitor-management">
        <div className="bg-white rounded-[10px] shadow-[0px_2px_10px_rgba(0,0,0,0.1)] overflow-hidden">
          <CustomTabsV2
            variant="fullWidth"
            indicatorColor='none'
            value={tabValue}
            onChange={handleChangeTab}
          >
            <Tab
              label={t('common.events')}
              {...a11yProps(0)}
              disableRipple
            />

            <Tab
              label={t('common.visitors')}
              {...a11yProps(1)}
              disableRipple
            />
          </CustomTabsV2>

          <CustomTabPanel value={tabValue} index={0}>
            <div className='p-4'>
              {viewMode === 'list' ? (
                <>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <CustomSearch
                        onSearchChange={handleQuickFilterChange}
                      />
                    </div>

                    <CustomSplitButton
                      label={t("button.add")}
                      startIcon={<AddIcon />}
                      onMainClick={handleOpenAddRoster}
                      onDropdownClick={handleToggleAdvanced}
                      ref={anchorRefAdvanced}
                    />
                  </div>

                  <div className="mt-4">
                    <LoadingProvider>
                      <VisitorRoster
                        usersData={usersData}
                        data={data}
                        refreshData={refreshData}
                        onRefresh={handleRefreshData}
                        onRowSelection={handleRosterRowSelection}
                        searchText={debouncedSearchText}
                        onPaginationChange={handlePaginationChange}
                      />
                    </LoadingProvider>
                  </div>
                </>
              ) : (
                <VisitorRosterDetail
                  data={selectedRosterDetail}
                  onBack={() => {
                    setViewMode('list');
                    setSelectedRosterDetail(null);
                  }}
                  usersData={usersData}
                  id={selectedRosterDetail?.id}
                  refreshData={refreshData}
                  onRefresh={handleRefreshData}
                />
              )}
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1}>
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4 2xl:gap-5 p-4">
              <LoadingProvider>
                <VisitorUserGridInfoTable
                  onRowClick={setSelectedRow}
                  onTotalElementsChange={setVisitorUserTotal}
                  refreshData={refreshData}
                  handleRefreshData={handleRefreshData}
                  refresh={refresh}
                />
              </LoadingProvider>

              <div className="card px-3 py-3">
                <VisitorUserInfo
                  selectedRow={selectedRow}
                  isListEmpty={visitorUserTotal === 0}
                  setSelectedRow={setSelectedRow}
                  refreshData={refreshData}
                  handleRefreshData={handleRefreshData}
                  refresh={refresh}
                />
              </div>
            </div>
          </CustomTabPanel>

        </div>
      </div>

      <AddVisitorEventDialog
        onRefresh={handleRefreshData}
        open={openAddRoster}
        handleClose={() => setOpenAddRoster(false)}
        usersData={usersData}
      />

      <Popper
        open={openAdvanced}
        anchorEl={anchorRefAdvanced.current}
        placement="bottom-start"
        transition
        disablePortal
        className="z-10"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "left top" : "left bottom",
            }}
          >
            <Paper style={{ borderRadius: "8px", }}>
              <ClickAwayListener onClickAway={handleCloseAdvanced}>
                <CustomAdvancedMenuList
                  id="advanced-menu"
                  aria-labelledby="advanced-button"
                >
                  <MenuItem
                    style={{
                      borderBottom: "1px solid #E5E7EB",
                      opacity: "0.5",
                      pointerEvents: "none",
                    }}
                  >
                    <Link>
                      <ImportIcon16pxSvg />
                      <p>{t("button.import-data")}</p>
                    </Link>
                  </MenuItem>

                  <MenuItem
                    onClick={exportEvents}
                    style={{
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <Link>
                      <ExportIcon16pxSvg />
                      <p>{t("button.export-data")}</p>
                    </Link>
                  </MenuItem>
                </CustomAdvancedMenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default VisitorManagement